import { evaluateFormCondition } from '@ayumia/component-runtime';
import { useId, useRef } from 'react';
import { Icon } from '../../design-system/icons';
import { Hint } from './inspector-controls';
import { text } from '../../app/i18n';
import { useDeveloperMode } from '../editor/developer-mode';
import type { ScopeVariable } from '../editor/condition-model';
import type { CustomEditor, CustomEditorProps } from '../editor/editor-types';
import { ConditionBuilder } from '../editor/ConditionBuilder';
import { LocalizedInput } from '../editor/PropertyPanel';
import type { Template } from './forms-model';

/**
 * Éditeurs dédiés du profil « formulaire » : conditions sur `$answers` (essayées par l'évaluateur unique
 * des formulaires, `evaluateFormCondition`), borne fixe ou calculée, règle de conformité partagée
 * (`rule_ref`, une par variante), valeurs d'une liste et leurs libellés, filtre d'un sélecteur.
 */

export interface ConformityRuleOption {
  test_id: string;
  min?: number;
  max?: number;
  unit?: string;
}

const isRecord = (value: unknown): value is Record<string, unknown> => value !== null && typeof value === 'object' && !Array.isArray(value);

/** Valeurs d'essai indexées par référence (`$answers.x`) → réponses (`{ x: … }`). */
export function answersOf(values: Readonly<Record<string, unknown>>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(values).map(([ref, value]) => [ref.replace(/^\$answers\./, ''), value]));
}

export function conditionEditor(scope: readonly ScopeVariable[], emptyMeaning: string): CustomEditor<Template> {
  return ({ label, value, onChange }) => (
    <ConditionBuilder label={label} value={value} onChange={(next) => onChange(next)} variables={scope} emptyMeaning={emptyMeaning} evaluate={(expression, values) => evaluateFormCondition(expression, answersOf(values))} />
  );
}

/** Borne : un nombre saisi, ou une expression calculée gardée telle quelle (lecture seule ici). */
export const boundEditor: CustomEditor<Template> = ({ label, value, onChange }) => <BoundInput label={label} value={value} onChange={onChange} />;

function BoundInput({ label, value, onChange }: Pick<CustomEditorProps<Template>, 'label' | 'value' | 'onChange'>) {
  const id = useId();
  if (value !== undefined && typeof value !== 'number') {
    return (
      <div className="ay-props__row">
        <span className="ay-field__label">{label}</span>
        <span className="ay-text">Borne calculée par une formule du gabarit (elle se reprend en mode développeur).</span>
        <button type="button" className="ay-button ay-button--secondary" onClick={() => onChange(undefined)}>Remplacer par une borne fixe</button>
      </div>
    );
  }
  return (
    <div className="ay-props__row">
      <label htmlFor={id} className="ay-field__label">{label}</label>
      <input id={id} type="number" inputMode="decimal" step="any" value={typeof value === 'number' ? value : ''} onChange={(event) => onChange(event.target.value === '' ? undefined : Number(event.target.value), label)} />
    </div>
  );
}

/** Calcul (Σ, rendement) : expression du dépôt, montrée ; la construction guidée d'un calcul viendra avec ADR-144. */
export const computeEditor: CustomEditor<Template> = ({ label, value, onChange }) => (
  <div className="ay-props__row">
    <span className="ay-field__label">{label}</span>
    {value === undefined ? <span className="ay-text ay-text--muted">Aucun : la valeur est saisie.</span> : <span className="ay-text">Valeur calculée par le serveur à partir d’autres réponses.</span>}
    {value !== undefined && <button type="button" className="ay-button ay-button--secondary" onClick={() => onChange(undefined)}>Retirer le calcul</button>}
    <Hint>Le serveur calcule ce champ et ignore toute valeur saisie.</Hint>
  </div>
);

/** Règle de conformité partagée : une règle active, ou une règle par variante choisie sur une réponse. */
export function ruleRefEditor(rules: readonly ConformityRuleOption[], scope: readonly ScopeVariable[]): CustomEditor<Template> {
  return ({ label, value, onChange }) => <RuleRefInput label={label} value={value} onChange={onChange} rules={rules} scope={scope} />;
}

const boundsText = (rule: ConformityRuleOption | undefined): string =>
  rule === undefined ? 'règle introuvable' : rule.min === undefined && rule.max === undefined ? 'sans limite' : `${rule.min ?? '−∞'} à ${rule.max ?? '+∞'}${rule.unit === undefined ? '' : ` ${rule.unit}`}`;

function RuleRefInput({ label, value, onChange, rules, scope }: Pick<CustomEditorProps<Template>, 'label' | 'value' | 'onChange'> & { rules: readonly ConformityRuleOption[]; scope: readonly ScopeVariable[] }) {
  const id = useId();
  const options = (selected: string) => (
    <>
      <option value="">—</option>
      {selected !== '' && !rules.some((rule) => rule.test_id === selected) && <option value={selected}>{selected} (introuvable)</option>}
      {rules.map((rule) => (
        <option key={rule.test_id} value={rule.test_id}>{`${rule.test_id} (${boundsText(rule)})`}</option>
      ))}
    </>
  );
  // Variante : `{ $if: [ condition, règle si vrai, règle sinon ] }`, une condition guidée sur une réponse.
  const variant = isRecord(value) && Array.isArray(value['$if']) && value['$if'].length === 3 && typeof value['$if'][1] === 'string' && typeof value['$if'][2] === 'string' ? (value['$if'] as [unknown, string, string]) : null;
  if (value !== undefined && typeof value !== 'string' && variant === null) {
    return (
      <div className="ay-props__row">
        <span className="ay-field__label">{label}</span>
        <span className="ay-text">Règle choisie par une formule du gabarit (elle se reprend en mode développeur).</span>
        <button type="button" className="ay-button ay-button--secondary" onClick={() => onChange(undefined)}>Remplacer par une règle</button>
      </div>
    );
  }
  if (variant !== null) {
    return (
      <fieldset className="ay-props__group">
        <legend className="ay-field__label">{label} — une règle par variante</legend>
        <ConditionBuilder label="Quand" value={variant[0]} onChange={(condition) => onChange({ $if: [condition ?? true, variant[1], variant[2]] })} variables={scope} evaluate={(expression, values) => evaluateFormCondition(expression, answersOf(values))} />
        <label className="ay-props__row">
          <span className="ay-field__label">Règle si vrai</span>
          <select value={variant[1]} onChange={(event) => onChange({ $if: [variant[0], event.target.value, variant[2]] })}>{options(variant[1])}</select>
        </label>
        <label className="ay-props__row">
          <span className="ay-field__label">Règle sinon</span>
          <select value={variant[2]} onChange={(event) => onChange({ $if: [variant[0], variant[1], event.target.value] })}>{options(variant[2])}</select>
        </label>
        <button type="button" className="ay-button ay-button--secondary" onClick={() => onChange(variant[1])}>Une seule règle</button>
      </fieldset>
    );
  }
  const selected = typeof value === 'string' ? value : '';
  return (
    <div className="ay-props__row">
      <label htmlFor={id} className="ay-field__label">{label}</label>
      <select id={id} value={selected} onChange={(event) => onChange(event.target.value === '' ? undefined : event.target.value)}>{options(selected)}</select>
      {selected !== '' && <span className="ay-props__help">Bornes servies : {boundsText(rules.find((rule) => rule.test_id === selected))}. La couche du client l’emporte sur celle du Pack.</span>}
      <button type="button" className="ay-button ay-button--secondary" disabled={scope.length === 0} onClick={() => onChange({ $if: [{ $eq: [scope[0]!.ref, true] }, selected || (rules[0]?.test_id ?? ''), selected || (rules[0]?.test_id ?? '')] })}>Une règle par variante</button>
    </div>
  );
}

/** Valeurs d'une liste et leurs libellés multilingues (`enum_values` et `enum_labels` ensemble). */
export const enumValuesEditor: CustomEditor<Template> = ({ value, onChange, document, pointer }) => {
  void document;
  void pointer;
  return <EnumValues value={value} onChange={onChange} />;
};

/**
 * Code d'une valeur tiré de son libellé (« Non conforme » → `non_conforme`), unique dans la liste : l'auteur
 * ne voit ni ne saisit jamais de code (ADR-146 addendum (15)).
 */
export function codeFromLabel(label: string, taken: readonly string[]): string {
  const base = label
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  const stem = base === '' ? 'valeur' : /^[a-z]/.test(base) ? base : `valeur_${base}`;
  let code = stem;
  let index = 2;
  while (taken.includes(code)) code = `${stem}_${index++}`;
  return code;
}

function EnumValues({ value, onChange }: { value: unknown; onChange: (value: unknown) => void }) {
  const developer = useDeveloperMode();
  // Valeurs ajoutées dans cette session : leur code suit le libellé tant qu'aucune réponse n'a pu les citer.
  const fresh = useRef(new Set<string>());
  const validation = isRecord(value) ? value : {};
  const values = Array.isArray(validation['enum_values']) ? (validation['enum_values'] as string[]) : [];
  const labels = isRecord(validation['enum_labels']) ? (validation['enum_labels'] as Record<string, Record<string, string>>) : {};
  const write = (nextValues: string[], nextLabels: Record<string, unknown>): void => {
    const next: Record<string, unknown> = { ...validation, enum_values: nextValues, enum_labels: nextLabels };
    if (nextValues.length === 0) delete next['enum_values'];
    if (Object.keys(nextLabels).length === 0) delete next['enum_labels'];
    onChange(Object.keys(next).length === 0 ? undefined : next);
  };
  const recode = (entry: string, code: string): void => {
    const nextLabels = { ...labels } as Record<string, unknown>;
    if (code !== entry && labels[entry] !== undefined) {
      nextLabels[code] = labels[entry];
      delete nextLabels[entry];
    }
    write(values.map((candidate) => (candidate === entry ? code : candidate)), nextLabels);
  };
  const relabel = (entry: string, label: string): void => {
    const nextLabels = { ...labels, [entry]: { ...(labels[entry] ?? {}), fr: label } } as Record<string, unknown>;
    if (!fresh.current.has(entry)) {
      write(values, nextLabels);
      return;
    }
    const code = codeFromLabel(label, values.filter((candidate) => candidate !== entry));
    fresh.current.delete(entry);
    fresh.current.add(code);
    nextLabels[code] = nextLabels[entry];
    if (code !== entry) delete nextLabels[entry];
    write(values.map((candidate) => (candidate === entry ? code : candidate)), nextLabels);
  };
  return (
    <fieldset className="ay-props__group">
      <legend className="ay-field__label">Valeurs de la liste</legend>
      {values.map((entry, index) => (
        <div key={index} className={developer ? 'ay-props__group' : undefined}>
          <span className="ay-props__list-row">
            <input aria-label={`Valeur ${index + 1}`} type="text" value={labels[entry]?.['fr'] ?? ''} placeholder="Libellé" onChange={(event) => relabel(entry, event.target.value)} />
            <button type="button" className="ay-editor__tool" aria-label={`Retirer la valeur ${text(labels[entry], 'fr', String(index + 1))}`} title="Retirer" onClick={() => {
              const nextLabels = { ...labels } as Record<string, unknown>;
              delete nextLabels[entry];
              fresh.current.delete(entry);
              write(values.filter((_, position) => position !== index), nextLabels);
            }}><Icon name="close" size={14} /></button>
          </span>
          {developer && (
            <>
              <label className="ay-props__row">
                <span className="ay-field__label">Code</span>
                <input aria-label={`Code de la valeur ${index + 1}`} type="text" value={entry} onChange={(event) => recode(entry, event.target.value)} />
              </label>
              <LocalizedInput label={`Libellé de la valeur ${index + 1}`} required={false} value={labels[entry]} onChange={(next) => {
                const nextLabels = { ...labels } as Record<string, unknown>;
                if (next === undefined) delete nextLabels[entry];
                else nextLabels[entry] = next;
                write(values, nextLabels);
              }} />
            </>
          )}
        </div>
      ))}
      <button type="button" className="ay-button ay-button--secondary" onClick={() => {
        const label = `Valeur ${values.length + 1}`;
        const code = codeFromLabel(label, values);
        fresh.current.add(code);
        write([...values, code], { ...labels, [code]: { fr: label } });
      }}>+ Valeur</button>
    </fieldset>
  );
}

/** Filtre d'un sélecteur : égalités `{ champ: { eq: valeur } }`, la forme gouvernée de la liste d'objets. */
export const selectorFilterEditor: CustomEditor<Template> = ({ label, value, onChange }) => <FilterInput label={label} value={value} onChange={onChange} />;

function FilterInput({ label, value, onChange }: Pick<CustomEditorProps<Template>, 'label' | 'value' | 'onChange'>) {
  const filter = isRecord(value) ? (value as Record<string, Record<string, unknown>>) : {};
  const rows = Object.entries(filter);
  const simple = rows.every(([, condition]) => isRecord(condition) && Object.keys(condition).length === 1 && typeof condition['eq'] === 'string');
  if (!simple) {
    return (
      <div className="ay-props__row">
        <span className="ay-field__label">{label}</span>
        <span className="ay-text">Filtre avancé, gardé tel quel (il se reprend en mode développeur).</span>
      </div>
    );
  }
  const write = (entries: Array<[string, string]>): void => {
    const next = Object.fromEntries(entries.filter(([field]) => field !== '').map(([field, eq]) => [field, { eq }]));
    onChange(Object.keys(next).length === 0 ? undefined : next);
  };
  const pairs = rows.map(([field, condition]) => [field, String(condition['eq'])] as [string, string]);
  return (
    <fieldset className="ay-props__group">
      <legend className="ay-field__label">{label}</legend>
      {pairs.map(([field, eq], index) => (
        <span key={index} className="ay-props__list-row">
          <input aria-label={`Champ du filtre ${index + 1}`} type="text" value={field} onChange={(event) => write(pairs.map((pair, position) => (position === index ? [event.target.value, pair[1]] : pair)))} />
          <span aria-hidden="true">=</span>
          <input aria-label={`Valeur du filtre ${index + 1}`} type="text" value={eq} onChange={(event) => write(pairs.map((pair, position) => (position === index ? [pair[0], event.target.value] : pair)))} />
          <button type="button" className="ay-editor__tool" aria-label={`Retirer le filtre ${index + 1}`} onClick={() => write(pairs.filter((_, position) => position !== index))}><Icon name="close" size={14} /></button>
        </span>
      ))}
      <button type="button" className="ay-button ay-button--secondary" onClick={() => write([...pairs, ['status', 'active']])}>+ Ajouter une égalité</button>
      <Hint>Affichage seulement : la commande vérifie la référence et le serveur borne la liste à l’entité de la tâche.</Hint>
    </fieldset>
  );
}
